import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { JobPhotoManager } from "./JobPhotoManager";
import { useToast } from "@/hooks/use-toast";
import { Image as ImageIcon, ExternalLink, Info } from "lucide-react";

interface JobInfo {
  id: string;
  job_number: number;
  title: string;
  status: string;
  customer_name: string;
  created_at: string;
}

interface AdminJobPhotoManagerProps {
  jobId: string;
}

export const AdminJobPhotoManager = ({ jobId }: AdminJobPhotoManagerProps) => {
  const [jobInfo, setJobInfo] = useState<JobInfo | null>(null);
  const [resolvedJobId, setResolvedJobId] = useState<string | null>(null);
  const [photoCount, setPhotoCount] = useState(0);
  const [isPhotoManagerOpen, setIsPhotoManagerOpen] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    fetchJobInfo();
    fetchPhotoCount();
  }, [jobId]);

  const fetchJobInfo = async () => {
    try {
      const { data, error } = await supabase
        .from('jobs')
        .select(`
          id,
          job_number,
          title,
          status,
          created_at,
          profiles!customer_id (
            full_name,
            email
          )
        `)
        .eq('id', jobId)
        .maybeSingle();

      if (error) throw error;

      if (data) {
        setJobInfo({
          id: data.id,
          job_number: data.job_number,
          title: data.title,
          status: data.status,
          customer_name: data.profiles?.full_name || data.profiles?.email || 'Unknown Customer',
          created_at: data.created_at,
        });
      }
    } catch (error: any) {
      console.error('Error fetching job info:', error);
      toast({
        title: "Error loading job information",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPhotoCount = async () => {
    try {
      const { count, error } = await supabase
        .from('job_photos')
        .select('*', { count: 'exact' })
        .eq('job_id', jobId);

      if (error) throw error;
      setPhotoCount(count || 0);
    } catch (error: any) {
      console.error('Error fetching photo count:', error);
    }
  };

  const handlePhotosUpdated = () => {
    fetchPhotoCount();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-muted-foreground">Loading job information...</div>
      </div>
    );
  }

  if (!jobInfo) {
    return (
      <Card className="max-w-md mx-auto">
        <CardContent className="pt-6">
          <div className="text-center">
            <Info className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-lg font-semibold mb-2">Job Not Found</h3>
            <p className="text-sm text-muted-foreground mb-4">
              The job with ID "{jobId}" could not be found.
            </p>
            <p className="text-xs text-muted-foreground">
              Please verify the job ID and try again.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ImageIcon className="h-5 w-5" />
            Job Photo Management
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Job Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-muted/30 rounded-lg">
            <div>
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Job Number
              </label>
              <p className="text-sm font-semibold">#{jobInfo.job_number}</p>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Status
              </label>
              <p className="text-sm">
                <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
                  jobInfo.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                  jobInfo.status === 'assigned' ? 'bg-blue-100 text-blue-800' :
                  jobInfo.status === 'in_progress' ? 'bg-orange-100 text-orange-800' :
                  jobInfo.status === 'completed' ? 'bg-green-100 text-green-800' :
                  jobInfo.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {jobInfo.status.replace('_', ' ').toUpperCase()}
                </span>
              </p>
            </div>
            <div className="md:col-span-2">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Job Title
              </label>
              <p className="text-sm font-medium">{jobInfo.title}</p>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Customer
              </label>
              <p className="text-sm">{jobInfo.customer_name}</p>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Created
              </label>
              <p className="text-sm">{new Date(jobInfo.created_at).toLocaleDateString()}</p>
            </div>
          </div>

          {/* Photo Statistics */}
          <div className="flex items-center justify-between p-4 bg-primary/5 rounded-lg">
            <div>
              <h4 className="font-semibold text-primary">Photo Count</h4>
              <p className="text-sm text-muted-foreground">
                {photoCount} photo{photoCount !== 1 ? 's' : ''} uploaded
              </p>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-primary">{photoCount}</div>
              <div className="text-xs text-muted-foreground">/ 5 max</div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <Button
              onClick={() => setIsPhotoManagerOpen(true)}
              className="flex-1"
            >
              <ImageIcon className="h-4 w-4 mr-2" />
              Manage Photos
            </Button>
            
            <Button
              variant="outline"
              onClick={() => window.open(`/admin/jobs/${jobId}`, '_blank')}
              className="flex items-center gap-2"
            >
              <ExternalLink className="h-4 w-4" />
              View Job
            </Button>
          </div>

          {/* Job ID Reference */}
          <div className="text-center pt-2 border-t">
            <p className="text-xs text-muted-foreground">
              Job ID: <code className="bg-muted px-2 py-1 rounded text-xs">{jobId}</code>
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Photo Manager Dialog */}
      <JobPhotoManager
        jobId={jobId}
        open={isPhotoManagerOpen}
        onOpenChange={(open) => {
          setIsPhotoManagerOpen(open);
          if (!open) {
            handlePhotosUpdated();
          }
        }}
        isCustomer={true} // Allow full management capabilities
      />
    </div>
  );
};